import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, FormControlLabel,
  Checkbox, Alert, Paper, LinearProgress
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { fetchCheckinConfig, submitCheckin } from './api';

const theme = createTheme({ palette: { mode: 'light' } });

const STEPS = { LOADING: 0, GEO: 1, FORM: 2, REASON: 3, SUBREASON: 4, CONFIRM: 5, DONE: 6, ERROR: 7 };

const generateIdempotencyKey = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const CheckInForm = ({ token }) => {
  const [step, setStep] = useState(STEPS.LOADING);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [coords, setCoords] = useState(null);
  const [idempotencyKey] = useState(generateIdempotencyKey);

  const [form, setForm] = useState({
    ownerName: '', petName: '', phone: '', email: '',
    parkingSpace: '', vehicle: '', comment: '',
    reasonId: '', subreasonId: '', answers: {},
  });

  // Load config
  useEffect(() => {
    fetchCheckinConfig(token)
      .then(cfg => {
        setConfig(cfg);
        if (!cfg.mobileCheckinEnabled) {
          setError('Mobile check-in is not currently available. Please see staff.');
          setStep(STEPS.ERROR);
          return;
        }
        // Only request geolocation if the practice has configured a geofence
        if (cfg.geofenceLat && cfg.geofenceLng) {
          setStep(STEPS.GEO);
        } else {
          setStep(STEPS.FORM);
        }
      })
      .catch(() => {
        setError('This check-in link is invalid or has expired.');
        setStep(STEPS.ERROR);
      });
  }, [token]);

  // Geolocation
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Your browser does not support location. Please ask staff to check you in.');
      setStep(STEPS.ERROR);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStep(STEPS.FORM);
      },
      () => {
        setError('Location permission was denied. Please allow location access and try again, or ask staff to check you in.');
        setStep(STEPS.ERROR);
      },
      { timeout: 10000 }
    );
  }, []);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setAnswer = (qId, value) => setForm(f => ({ ...f, answers: { ...f.answers, [qId]: value } }));

  const activeQuestions = config?.questions?.filter(q => q.active !== false) || [];
  const selectedReason = config?.reasons?.find(r => r.id === form.reasonId);
  const subreasons = selectedReason?.subreasons || [];

  const handleFormNext = () => {
    if (!form.ownerName.trim() || !form.petName.trim()) {
      setError('Owner name and pet name are required.');
      return;
    }
    setError('');
    if (config?.reasons?.length) setStep(STEPS.REASON);
    else setStep(STEPS.CONFIRM);
  };

  const handleReasonNext = () => {
    if (subreasons.length) setStep(STEPS.SUBREASON);
    else setStep(STEPS.CONFIRM);
  };

  const handleSubmit = async () => {
    setStep(STEPS.LOADING);
    try {
      await submitCheckin(token, {
        ...form,
        lat: coords?.lat,
        lng: coords?.lng,
        idempotencyKey,
      });
      setStep(STEPS.DONE);
    } catch (e) {
      setError(e.response?.data?.errors || 'Check-in failed. Please try again or ask staff for help.');
      setStep(STEPS.ERROR);
    }
  };

  const siteTitle = config?.siteTitle || 'Check In';

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 4, px: 2 }}>
        <Paper sx={{ width: '100%', maxWidth: 480, p: 3 }}>
          <Typography variant="h5" fontWeight={500} sx={{ mb: 3 }}>{siteTitle}</Typography>

          {/* Loading */}
          {step === STEPS.LOADING && <LinearProgress />}

          {/* Geolocation prompt */}
          {step === STEPS.GEO && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                To check in, we need to confirm you're at the practice location. Please allow location access when prompted.
              </Typography>
              <Button variant="contained" fullWidth onClick={requestLocation}>
                Allow Location &amp; Continue
              </Button>
            </Box>
          )}

          {/* Main form */}
          {step === STEPS.FORM && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Your Name" required fullWidth value={form.ownerName} onChange={e => set('ownerName', e.target.value)} inputProps={{ autoCapitalize: 'words' }} />
              <TextField label="Pet Name" required fullWidth value={form.petName} onChange={e => set('petName', e.target.value)} inputProps={{ autoCapitalize: 'words' }} />
              <TextField label="Phone Number" fullWidth value={form.phone} onChange={e => set('phone', e.target.value)} inputProps={{ inputMode: 'tel' }} />
              <TextField label="Email" fullWidth value={form.email} onChange={e => set('email', e.target.value)} inputProps={{ inputMode: 'email' }} />
              <TextField label="Parking Space #" fullWidth value={form.parkingSpace} onChange={e => set('parkingSpace', e.target.value)} />
              <TextField label="Vehicle Description" fullWidth value={form.vehicle} onChange={e => set('vehicle', e.target.value)} />
              {activeQuestions.map(q => (
                q.type === 'yes_no' ? (
                  <FormControlLabel key={q.id}
                    control={<Checkbox checked={!!form.answers[q.id]} onChange={e => setAnswer(q.id, e.target.checked)} />}
                    label={q.label + (q.required ? ' *' : '')}
                  />
                ) : (
                  <TextField key={q.id} label={q.label} required={q.required} fullWidth
                    multiline={q.type === 'long_text'} rows={q.type === 'long_text' ? 3 : 1}
                    value={form.answers[q.id] || ''}
                    onChange={e => setAnswer(q.id, e.target.value)}
                  />
                )
              ))}
              <TextField label="Comments" fullWidth multiline rows={2} value={form.comment} onChange={e => set('comment', e.target.value)} />
              <Button variant="contained" size="large" fullWidth onClick={handleFormNext}>Next</Button>
            </Box>
          )}

          {/* Reason selection */}
          {step === STEPS.REASON && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">Reason for visit</Typography>
              {config.reasons.map(r => (
                <Button key={r.id} variant={form.reasonId === r.id ? 'contained' : 'outlined'}
                  fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => { set('reasonId', r.id); set('subreasonId', ''); }}>
                  {r.label}
                </Button>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button fullWidth onClick={() => setStep(STEPS.FORM)}>Back</Button>
                <Button fullWidth variant="contained" disabled={!form.reasonId} onClick={handleReasonNext}>Next</Button>
              </Box>
            </Box>
          )}

          {/* Subreason selection */}
          {step === STEPS.SUBREASON && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">{selectedReason?.label} — select a category</Typography>
              {subreasons.map(s => (
                <Button key={s.id} variant={form.subreasonId === s.id ? 'contained' : 'outlined'}
                  fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => set('subreasonId', s.id)}>
                  {s.label}
                </Button>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button fullWidth onClick={() => setStep(STEPS.REASON)}>Back</Button>
                <Button fullWidth variant="contained" disabled={!form.subreasonId} onClick={() => setStep(STEPS.CONFIRM)}>Next</Button>
              </Box>
            </Box>
          )}

          {/* Confirm */}
          {step === STEPS.CONFIRM && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">Confirm your details</Typography>
              <Box sx={{ bgcolor: '#f9f9f9', borderRadius: 1, p: 2 }}>
                <Typography><strong>Name:</strong> {form.ownerName}</Typography>
                <Typography><strong>Pet:</strong> {form.petName}</Typography>
                {form.phone && <Typography><strong>Phone:</strong> {form.phone}</Typography>}
                {form.parkingSpace && <Typography><strong>Parking Space:</strong> {form.parkingSpace}</Typography>}
                {form.reasonId && <Typography><strong>Reason:</strong> {selectedReason?.label}{form.subreasonId && ` — ${subreasons.find(s => s.id === form.subreasonId)?.label}`}</Typography>}
                {form.comment && <Typography><strong>Comment:</strong> {form.comment}</Typography>}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth onClick={() => setStep(config?.reasons?.length ? STEPS.REASON : STEPS.FORM)}>Back</Button>
                <Button fullWidth variant="contained" size="large" onClick={handleSubmit}>Check In</Button>
              </Box>
            </Box>
          )}

          {/* Done */}
          {step === STEPS.DONE && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" gutterBottom>✓ You're checked in!</Typography>
              <Typography color="text.secondary">
                Please remain in your vehicle or wait nearby. A staff member will call you in shortly.
              </Typography>
              {config?.hoursNote && <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">{config.hoursNote}</Typography>}
            </Box>
          )}

          {/* Error */}
          {step === STEPS.ERROR && (
            <Box>
              <Alert severity="error">{error}</Alert>
              <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
                Please speak with a staff member for assistance.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default CheckInForm;
